import React from 'react';
import { FormattedMessage } from 'react-intl';
import { reduxForm } from 'redux-form';
import { Col, Row } from 'react-bootstrap';
import messages from '../../ApplicationProfile/LeaseApplicationTab/messages';
import CreditCriminalStatus from '../../ApplicationProfile/LeaseApplicationTab/CreditCriminalStatus';
import { LeaseApplication } from '../../ApplicationProfile/LeaseApplicationTab';
import ApprovedResidentContainer from './ApprovedResidentContainer/index';
import type { DecisionStatus } from '../../ApplicationProfile/types';
import type { DropdownOption, User } from '../../App/types';
import ErrorMessage from '../../../components/ErrorMessage';
import { useFlags } from 'launchdarkly-react-client-sdk';
import TheWorkNumber from '../../../components/TheWorkNumber';
import { useOneProperty } from '../../../hooks/data-fetching/useOneProperty';
import ApplicationChecklist from '../../ApplicationProfile/LeaseApplicationTab/ApplicationChecklist';
import LeaseApplicationStatusTracker from '../../../components/LeaseApplicationStatusTracker';
import { useProviderPropertyProfiles } from '../../ApplicationProfile/LeaseApplicationTab/hooks.queries';
import CreditCriminalStatusScreening from '../../../components/CreditCriminalStatusScreening';
import HouseholdApplicationsDrawer from '../../../components/HouseholdApplicationsDrawer';
import PendingAndCanceledApplicationsDrawer from '../../../components/PendingAndCanceledApplicationsDrawer';

type Props = {
  intl: Object,
  applicationDecisionStatus: Array<DecisionStatus>,
  applicationStatuses: Array<DropdownOption>,
  applicationId: string,
  screening: Object,
  screeningLetterTypes: Array<Object>,
  generateAdverseActionLetter: Function,
  currentUser: User,
  currentRecord: any,
  handleChecklistOptionChange?: Function,
  handleEditApplicationForm?: Function,
  applicants: Object,
  isResident: boolean,
  checkUserPermissions: Function,
  pendingApplicants: Array<any>,
  canceledApplicants: Array<any>,
  approvedApplicants: Array<any>,
  applicantCancellationReasons: Array<any>,
  approveApplicant: Function,
  cancelApplicant: Function,
  filteredResidents: Array<any>,
  selectedProperty: Object,
  householdId: string,
  initialValues: { applicationStatusId: string },
  mainCustomerId: string,
  handleMultipleChecklistOptionsChange?: Function,
  approveApplicantAction?: Function,
  cancelApplicantAction?: Function,
  householdMembers?: Array<any>,
};

export const CompletionStatusResidentApproved = () => {
  return (
    <div className="container-fluid">
      <Row>
        <Col md={7} xs={12}>
          <h2 className="appmanagertitle">
            <FormattedMessage {...messages.approvedApplications} />
          </h2>
        </Col>
      </Row>
    </div>
  );
};

export const CompletionStatusResident = ({ title, message }: any) => {
  return (
    <div className="container-fluid">
      <Row>
        <h2 className="appmanagertitle padtop20">
          <FormattedMessage {...title} />{' '}
        </h2>
        {message && (
          <small className="padleft10">
            <FormattedMessage {...message} />
          </small>
        )}
      </Row>
    </div>
  );
};

export const LeaseApplicationTab = ({
  intl,
  currentRecord,
  applicationDecisionStatus,
  applicationStatuses,
  handleChecklistOptionChange,
  handleEditApplicationForm,
  approveApplicant,
  cancelApplicant,
  applicationId,
  screening,
  checkUserPermissions,
  currentUser,
  pendingApplicants,
  canceledApplicants,
  applicantCancellationReasons,
  filteredResidents,
  generateAdverseActionLetter,
  screeningLetterTypes,
  selectedProperty,
  householdId,
  initialValues: { applicationStatusId },
  handleMultipleChecklistOptionsChange,
  approveApplicantAction,
  cancelApplicantAction,
  householdMembers,
  mainCustomerId,
}: Props) => {
  const {
    theWorkNumber,
    transunionOffLeaseAppOff,
    leaseAppRevampTransunion,
    leaseAppRevampTruework,
    leaseAppRevampFullTab,
    affordaPortaV1,
    renewalScreenings,
  } = useFlags();
  let organizationId = currentUser?.user?.organization?.id ?? '';
  let propertyId = selectedProperty?.id ?? '';
  const theWorkNumberProps = {
    organizationId,
    propertyId,
    householdId: householdId ?? '',
    intl,
    currentUser,
  };
  const propertyDetails = useOneProperty(organizationId, propertyId);
  const isTransUnionActive = propertyDetails[0]?.isTransUnionActive;
  const noApplicantToDisplayMessage = 'There are no applications to display';

  const shouldHideScreening =
    leaseAppRevampTruework === true || leaseAppRevampTransunion === true;

  const { providerPropertyProfiles } = useProviderPropertyProfiles({
    propertyId,
    enabled: shouldHideScreening,
  });

  const applicationStatus = applicationStatuses?.find(
    (status) => status.value === applicationStatusId,
  );

  const isApplicationCanceledOrDenied =
    applicationStatus?.text === 'Canceled' ||
    applicationStatus?.text === 'Denied';

  const adverseActionDeadlineDays =
    selectedProperty?.config?.adverseActionDeadlineDays;

  return (
    <div className="tab-pane active" id="1a">
      {!leaseAppRevampFullTab && (
        <LeaseApplication
          intl={intl}
          currentRecord={currentRecord}
          applicationDecisionStatus={applicationDecisionStatus}
          applicationStatuses={applicationStatuses}
          isResident={true}
          checkUserPermissions={checkUserPermissions}
          applicationStatusId={applicationStatusId}
        />
      )}
      {leaseAppRevampFullTab && (
        <>
          <LeaseApplicationStatusTracker
            application={currentRecord}
            providerPropertyProfiles={providerPropertyProfiles ?? []}
            householdId={householdId}
            propertyId={propertyId}
            organizationId={organizationId}
            property={selectedProperty}
            isResident
            checkUserPermissions={checkUserPermissions}
            applicationStatuses={applicationStatuses}
            applicationStatusId={applicationStatusId}
          />
          <HouseholdApplicationsDrawer
            applicants={currentRecord?.applicants ?? []}
            propertyId={propertyId}
            organizationId={organizationId}
            isResident
            applicationDecisionStatus={applicationDecisionStatus}
            householdMembers={householdMembers}
            mainCustomerId={mainCustomerId}
            application={currentRecord}
            selectedProperty={selectedProperty}
            affordaPortaV1={affordaPortaV1}
            currentUserId={currentUser?.user?.id}
          />
        </>
      )}
      {shouldHideScreening ? (
        <CreditCriminalStatusScreening
          providerPropertyProfiles={providerPropertyProfiles ?? []}
          householdId={householdId}
          applicationId={applicationId}
          isApplicationCanceledOrDenied={isApplicationCanceledOrDenied}
          applicationStatus={applicationStatus?.text}
          intl={intl}
          adverseActionDeadlineDays={adverseActionDeadlineDays}
          screeningLetterTypes={screeningLetterTypes}
          organizationId={organizationId}
          propertyId={propertyId}
          promptToaster={() => {}}
          isResident
        />
      ) : (
        <CreditCriminalStatus
          intl={intl}
          applicationId={applicationId}
          screening={screening}
          generateAdverseActionLetter={generateAdverseActionLetter}
          screeningLetterTypes={screeningLetterTypes}
          currentUser={currentUser}
          isResident
          isTransUnionActive={isTransUnionActive}
          transunionOffLeaseAppOff={transunionOffLeaseAppOff}
          adverseActionDeadlineDays={adverseActionDeadlineDays}
        />
      )}
      {leaseAppRevampFullTab && (
        <PendingAndCanceledApplicationsDrawer
          pendingApplicants={pendingApplicants ?? []}
          canceledApplicants={canceledApplicants ?? []}
          propertyId={propertyId}
          organizationId={organizationId}
          applicationDecisionStatus={applicationDecisionStatus}
          approveApplicant={approveApplicantAction}
          cancelApplicant={cancelApplicantAction}
          deniedReasons={applicantCancellationReasons}
          handleMultipleChecklistOptionsChange={
            handleMultipleChecklistOptionsChange
          }
          handleEditApplicationForm={handleEditApplicationForm}
          selectedProperty={selectedProperty}
          applicationId={applicationId}
          infoMessage={
            renewalScreenings && (pendingApplicants ?? []).length > 0
              ? intl.formatMessage(messages.newMemberScreeningInfo)
              : undefined
          }
        />
      )}
      {theWorkNumber && <TheWorkNumber {...theWorkNumberProps} />}
      {!leaseAppRevampFullTab && (
        <>
          <CompletionStatusResidentApproved intl={intl} />
          <ApprovedResidentContainer
            {...{
              intl,
              currentRecord: { approvedApplicants: filteredResidents },
            }}
          />
          <CompletionStatusResident
            title={messages.pendingApplications}
            message={messages.cannotScreenPending}
          />
          <ApplicationChecklist
            {...{
              applicationDecisionStatus,
              approveApplicant,
              cancelApplicant,
              currentRecord: { applicants: pendingApplicants },
              deniedReasons: applicantCancellationReasons,
              handleChecklistOptionChange,
              handleEditApplicationForm,
              intl,
              isResident: false,
            }}
          />

          {pendingApplicants.length === 0 && (
            <ErrorMessage message={noApplicantToDisplayMessage} />
          )}
          <CompletionStatusResident title={messages.canceledApplications} />
          <ApplicationChecklist
            {...{
              applicationDecisionStatus,
              approveApplicant,
              cancelApplicant,
              currentRecord: { applicants: canceledApplicants },
              deniedReasons: applicantCancellationReasons,
              handleChecklistOptionChange,
              handleEditApplicationForm,
              intl,
              isCanceled: true,
              isResident: false,
            }}
          />
          {canceledApplicants.length === 0 && (
            <ErrorMessage message={noApplicantToDisplayMessage} />
          )}
        </>
      )}
    </div>
  );
};

export default reduxForm({
  form: 'LeaseApplicationTab',
  enableReinitialize: true,
})(LeaseApplicationTab);
